import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TableContainer, Paper, Table, TableRow, TableHead, TableCell, TableBody, CircularProgress, styled, Backdrop } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { fetchAuthorsFromFirestore, getAuthorsFromCache, addAuthorToFirestore, updateAuthorInFirestore, deleteAuthorFromFirestore, updateCacheWithAuthors } from 'app/services/AuthorService';
import { ConfirmationDialog, PageLayout, SimpleCard } from 'app/components';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

const TextField = styled(TextValidator)(() => ({
    width: "100%",
    marginBottom: "16px"
}));

const AuthorList = () => {
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentAuthor, setCurrentAuthor] = useState({ name: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Load the list of authors from Firestore or from the cache
    const loadAuthors = async () => {
        try {
            setLoading(true);

            // const cachedAuthors = getAuthorsFromCache();
            // if (cachedAuthors.length > 0) {
            //     setAuthors(cachedAuthors); // If cached authors exist, use them first
            // } else {
            // If not in cache, fetch from Firestore
            const authorsFromFirestore = await fetchAuthorsFromFirestore();
            setAuthors(authorsFromFirestore);
            // }
        } catch (error) {
            console.error("Error loading authors:", error);
            enqueueSnackbar("An error occurred while loading authors.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Load the list of authors from Firestore
    const loadAuthorsFromFirestore = async () => {
        try {
            setLoading(true);

            // Fetch authors from Firestore directly
            const authorsFromFirestore = await fetchAuthorsFromFirestore();

            // Update the authors list in state
            setAuthors(authorsFromFirestore);

            // Optionally, update the cache with the new authors list
            // updateCacheWithAuthors(authorsFromFirestore);
        } catch (error) {
            console.error("Error loading authors:", error);
            enqueueSnackbar("An error occurred while loading authors.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAuthors();
    }, []);

    // Handle saving a new or updated author
    const handleSave = async () => {
        if (!currentAuthor.name.trim()) {
            enqueueSnackbar("Author name cannot be empty!", { variant: "error" });
            return;
        }

        try {
            setLoading(true);

            if (isEdit) {
                await updateAuthorInFirestore(currentAuthor.id, currentAuthor.name);
            } else {
                await addAuthorToFirestore(currentAuthor.name);
            }

            // Reload the authors list and update the cache
            const updatedAuthors = await fetchAuthorsFromFirestore();
            setAuthors(updatedAuthors);
            // updateCacheWithAuthors(updatedAuthors);

            enqueueSnackbar("Author saved successfully!", { variant: "success" });
            setOpenDialog(false);
        } catch (error) {
            console.error("Error saving author:", error);
            enqueueSnackbar("Failed to save author!", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Handle deleting an author
    const handleDelete = async (author) => {
        setCurrentAuthor(author);
        setDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            await deleteAuthorFromFirestore(currentAuthor.id);

            // Update the authors list after deletion
            const updatedAuthors = await fetchAuthorsFromFirestore();
            setAuthors(updatedAuthors);
            // updateCacheWithAuthors(updatedAuthors);

            enqueueSnackbar("Author deleted!", { variant: "success" });
        } catch (error) {
            console.error("Error deleting author:", error);
            enqueueSnackbar("Failed to delete author!", { variant: "error" });
        } finally {
            setLoading(false);
            setDeleteDialog(false);
        }
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog(false);
    };

    const handleRefresh = async () => {
        await loadAuthorsFromFirestore();
    };

    return (
        <PageLayout routeSegments={[{ name: "Authors" }]}>
            <SimpleCard title="Author List">
                <Box width="100%" overflow="auto">
                    <Button variant="contained" color="primary" onClick={() => { setCurrentAuthor({ name: '' }); setIsEdit(false); setOpenDialog(true); }} style={{ marginBottom: '16px', float: 'right' }}>
                        Add New Author</Button>
                    <Box>
                        {/* <Button variant="contained" color="primary" onClick={handleRefresh} style={{ marginBottom: '16px', float: 'right' }}>
                            Refresh
                        </Button> */}
                        <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Author ID</TableCell>
                                        <TableCell>Author Name</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {authors.map((author) => (
                                        <TableRow key={author.id}>
                                            <TableCell>{author.id}</TableCell>
                                            <TableCell>{author.name}</TableCell>
                                            <TableCell align="right">
                                                <IconButton onClick={() => { setCurrentAuthor(author); setIsEdit(true); setOpenDialog(true); }} color="primary">
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton onClick={() => handleDelete(author)} color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    {/* Author Add / Edit Dialog */}
                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                        <DialogTitle>{isEdit ? "Edit Author" : "Add New Author"}</DialogTitle>
                        <ValidatorForm onSubmit={handleSave} onError={() => null}>
                            <DialogContent>
                                <TextField
                                    label="Author Name"
                                    fullWidth
                                    margin="normal"
                                    value={currentAuthor.name}
                                    onChange={(e) => setCurrentAuthor({ ...currentAuthor, name: e.target.value })}
                                    validators={["required"]}
                                    errorMessages={["This field is required"]}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                                <Button type="submit" color="primary">
                                    {isEdit ? "Update" : "Add"}
                                </Button>
                                <Backdrop
                                    open={loading}
                                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                                    <CircularProgress color="inherit" />
                                </Backdrop>
                            </DialogActions>
                        </ValidatorForm>
                    </Dialog>
                    <ConfirmationDialog
                        title={"Confirm Delete"}
                        text={'Are you sure you want to delete this item?'}
                        open={deleteDialog}
                        onConfirmDialogClose={handleCloseDeleteDialog}
                        onYesClick={handleConfirmDelete}
                    />
                </Box>
            </SimpleCard>
        </PageLayout >
    );
};

export default AuthorList;
